import React, { useState } from 'react';
import { ImageUploader } from '~/components/image-uploader';
import { Separator } from '~/components/separator';
import { addFileProps } from '~/utils/files';
import type { UploadedImage } from '~/utils/files';
import { trackError } from '~/utils/tracking';
import { LMIProofDocTypeSelect } from '../lmi-proof-doc-type-select';
import { handleUpload } from '../utils/handle-uploads';
import type { LMIProofDoc } from './lmi-proof-docs-upload.component';

interface LMIProofDocUploaderProps {
  documentTypes: string[];
  docs: LMIProofDoc[];
  pending: boolean;
  allDocumentTypes: string[] | Record<string, string>;
  onDocsChange: (docs: LMIProofDoc[]) => void;
  onDocumentTypesChange: (types: string[]) => void;
  onFailureChange: (failure: boolean) => void;
  required?: boolean;
}

export const LMIProofDocUploader: React.FC<LMIProofDocUploaderProps> = ({
  documentTypes,
  docs,
  pending,
  allDocumentTypes,
  onDocsChange,
  onDocumentTypesChange,
  onFailureChange,
  required = false,
}) => {
  const [showImageDropZone, setShowImageDropZone] = useState(false);
  const showChooseAnotherProgram = documentTypes.every(documentType =>
    docs.some(doc => doc.documentType === documentType),
  );

  const addDocumentType = (documentType: string) => {
    onDocumentTypesChange([...documentTypes, documentType]);
    setShowImageDropZone(true);
  };

  const replaceDocumentType = (oldDocumentType: string, newDocumentType: string) => {
    setShowImageDropZone(true);
    const newDocumentTypes = [...documentTypes];
    const index = newDocumentTypes.indexOf(oldDocumentType);
    newDocumentTypes[index] = newDocumentType;
    onDocumentTypesChange(newDocumentTypes);
  };

  return (
    <>
      {documentTypes.map((documentType, index) => {
        const uploadedImages = docs
          .filter(doc => doc.documentType === documentType)
          .map(doc => {
            return {
              ...doc.image,
              uploadId: Date.now(),
            } as UploadedImage;
          });

        return (
          <React.Fragment key={documentType}>
            <LMIProofDocTypeSelect
              required={required}
              label={
                !documentTypes.length || documentTypes[0] === documentType
                  ? 'Choose program'
                  : 'Choose another program (optional)'
              }
              allDocumentTypes={allDocumentTypes}
              addedDocumentTypes={documentTypes}
              selectedDocumentType={documentType}
              disabled={uploadedImages.length > 0}
              onChange={newDocumentType => replaceDocumentType(documentType, newDocumentType)}
            />
            {!!showImageDropZone && (
              <ImageUploader
                acceptFileTypes={{
                  'image/jpg': ['.jpg', '.jpeg', '.png', '.heic'],
                }}
                copy={{ documentType: 'document' }}
                uploadedImages={uploadedImages}
                maxImages={2}
                pageType="front-back"
                onDelete={docType => {
                  onFailureChange(false);
                  onDocsChange(docs.filter(doc => doc.image.id !== docType));
                }}
                onUploadFailure={fileRejection => {
                  onFailureChange(true);
                  onDocsChange([
                    ...docs,
                    {
                      documentType,
                      image: addFileProps(fileRejection),
                    },
                  ]);
                }}
                onUploadSuccess={async (file, base64Image) => {
                  try {
                    onFailureChange(false);
                    const newDoc: LMIProofDoc = await handleUpload({
                      file,
                      base64Image,
                      documentType,
                    });
                    onDocsChange([...docs, newDoc]);
                  } catch (error) {
                    trackError(new Error('Error uploading proof document'), {
                      extras: {
                        documentType,
                        error,
                      },
                    });
                    onFailureChange(true);
                  }
                }}
                pending={pending}
              />
            )}
            {!!(index < documentTypes.length - 1 || showChooseAnotherProgram) && (
              <Separator pt={4} pb={4} />
            )}
          </React.Fragment>
        );
      })}
      {!!showChooseAnotherProgram && (
        <LMIProofDocTypeSelect
          required={false}
          label={!documentTypes.length ? 'Choose program' : 'Choose another program (optional)'}
          allDocumentTypes={allDocumentTypes}
          addedDocumentTypes={documentTypes}
          onChange={addDocumentType}
        />
      )}
    </>
  );
};
